import { Video, usePlayerFromContext } from '@loomhq/createhq';
import { FillerRange } from '@loomhq/createhq/dist/video-player/api/filler-volume';
import { useEffect } from 'react';
import {
  FillerWordRemovalOption,
  SUCCESS,
  TEXT_AUDIO,
  TranscriptionStatus,
} from 'constants/videoTranscript';
import FetchVideoTranscript from 'creators/FetchVideoTranscript.graphql';
import { getGraphQLClient } from 'utilities/graphql';

import { usePasswordContext } from '../password/context';

interface TranscriptPhrasesResponse {
  filler_words: {
    ranges: FillerRange[];
  };
  filler_word_removal: FillerWordRemovalOption;
  transcription_status: TranscriptionStatus;
}

function useMuteFillerWords(video: Video): void {
  const player = usePlayerFromContext();
  const { password } = usePasswordContext();

  useEffect(() => {
    if (!player) {
      return;
    }

    const transcriptFetch = async () => {
      const videoTranscript = await fetchTranscriptPhrases(video, password);

      if (!videoTranscript) {
        return;
      }

      const {
        filler_words: fillerWords,
        filler_word_removal: fillerWordRemoval,
        transcription_status: transcriptStatus,
      } = videoTranscript;

      if (fillerWordRemoval === TEXT_AUDIO && transcriptStatus === SUCCESS) {
        player.enableFillerVolume(fillerWords.ranges);
      } else {
        player.disableFillerVolume();
      }
    };

    transcriptFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, video.modelId]);
}

async function fetchTranscriptPhrases(
  video: Video,
  password: string
): Promise<TranscriptPhrasesResponse | null> {
  const client = getGraphQLClient();

  const { data } = await client.query({
    query: FetchVideoTranscript,
    variables: {
      videoId: video.modelId,
      password,
    },
  });

  const { fetchVideoTranscript } = data;

  if (fetchVideoTranscript['__typename'] === 'GenericError') {
    return null;
  }

  return fetchVideoTranscript;
}

export default useMuteFillerWords;
